import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, FileSpreadsheet, Loader2, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { EtatHistorique } from './EtatHistorique';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/impayes`;
const eur = (v) => `${Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR') : '—');
const FILTRES = [['', 'Tous'], ['rcds', 'RCDS'], ['domium', 'DOS-DOMIUM']];

const Ligne = ({ r, onRelance, busy }) => (
  <tr className="border-t border-slate-100 align-top" data-testid={`impaye-row-${r.produit}-${r.id}`}>
    <td className="py-2 pr-2"><p className="font-medium text-slate-800">{r.nom || '—'}</p><p className="text-[11px] text-slate-500">{r.email}</p>
      <EtatHistorique items={r.historique} testId={`impaye-historique-${r.id}`} /></td>
    <td className="py-2 pr-2 text-xs"><p>{r.produit_label}</p><p className="font-mono text-[11px] text-slate-500">{r.reference}</p></td>
    <td className="py-2 pr-2"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${r.etat === 'resilie' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}`} data-testid={`impaye-etat-${r.id}`}>{r.etat_label}</span></td>
    <td className="py-2 pr-2 text-xs">{r.nb_retard} · depuis le {fmtD(r.date_premier_retard)}<br /><span className="text-slate-500">{r.jours_retard} j de retard</span></td>
    <td className="py-2 pr-2 text-xs text-right">{eur(r.echeances_dues)}{r.contribution_suspensive > 0 && <><br /><span className="text-slate-500">+ {eur(r.contribution_suspensive)} susp.</span></>}</td>
    <td className="py-2 pr-2 text-right font-semibold" data-testid={`impaye-total-${r.id}`}>{eur(r.total_a_regler)}</td>
    <td className="py-2 text-right">
      <Button size="sm" variant="outline" onClick={() => onRelance(r)} disabled={busy === r.id} className="h-7 text-xs gap-1 border-emerald-300 text-emerald-700" data-testid={`impaye-relance-btn-${r.id}`}>
        {busy === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Relancer
      </Button>
      {r.derniere_relance_at && <p className="text-[10px] text-slate-400 mt-1">Relancé le {fmtD(r.derniere_relance_at)}</p>}
    </td>
  </tr>
);

export const ImpayesTable = ({ getAuthHeader, defaultProduit = '' }) => {
  const [open, setOpen] = useState(false);
  const [produit, setProduit] = useState(defaultProduit);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => {
    setData(null);
    axios.get(API, { headers: getAuthHeader(), params: { produit } }).then((r) => setData(r.data)).catch(() => setData({ items: [], error: true }));
  }, [getAuthHeader, produit]);
  useEffect(() => { if (open) load(); }, [open, load]);
  const relance = async (r) => {
    setBusy(r.id);
    try {
      const { data: res } = await axios.post(`${API}/${r.produit}/${encodeURIComponent(r.id)}/relance`, { origin: window.location.origin }, { headers: getAuthHeader() });
      toast.success(`Relance envoyée à ${res.email} (${eur(res.montant)}, lien valable 7 jours)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Relance impossible'); } finally { setBusy(null); }
  };
  const exporter = async () => {
    setBusy('export');
    try {
      const r = await axios.get(`${API}/export.xlsx`, { headers: getAuthHeader(), params: { produit }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      Object.assign(document.createElement('a'), { href: url, download: `impayes_${produit || 'tous'}_${new Date().toISOString().slice(0, 10)}.xlsx` }).click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch { toast.error('Export impossible'); } finally { setBusy(null); }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="gap-1.5 border-red-300 text-red-700" data-testid="impayes-open-btn">
        <AlertTriangle className="h-4 w-4" /> Impayés &amp; résiliations
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="impayes-dialog">
          <DialogHeader><DialogTitle>Contrats impayés et résiliés</DialogTitle></DialogHeader>
          <div className="flex flex-wrap items-center gap-2">
            {FILTRES.map(([v, l]) => (
              <Button key={v || 'tous'} size="sm" variant={produit === v ? 'default' : 'outline'} onClick={() => setProduit(v)} className="h-7 text-xs" data-testid={`impayes-filtre-${v || 'tous'}`}>{l}</Button>
            ))}
            <Button size="sm" variant="outline" onClick={exporter} disabled={busy === 'export'} className="h-7 text-xs gap-1 border-emerald-300 text-emerald-700" data-testid="impayes-export-btn">
              {busy === 'export' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />} Exporter Excel
            </Button>
            {data && !data.error && (
              <span className="ml-auto text-xs text-slate-600" data-testid="impayes-synthese">
                {data.nb_impayes} impayé(s) · {data.nb_resilies} résilié(s) · total à régulariser <strong>{eur(data.total_a_regler)}</strong>
              </span>
            )}
          </div>
          {!data ? <Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto my-8" />
            : !data.items.length ? <p className="text-sm text-slate-500 text-center py-8" data-testid="impayes-vide">{data.error ? 'Liste indisponible.' : 'Aucun contrat impayé ou résilié.'}</p>
              : (
                <table className="w-full text-sm" data-testid="impayes-table">
                  <thead><tr className="text-left text-[11px] uppercase text-slate-500">
                    <th className="pb-1">Membre</th><th className="pb-1">Produit</th><th className="pb-1">État</th><th className="pb-1">Retard</th>
                    <th className="pb-1 text-right">Dû</th><th className="pb-1 text-right">À régler</th><th />
                  </tr></thead>
                  <tbody>{data.items.map((r) => <Ligne key={`${r.produit}-${r.id}`} r={r} onRelance={relance} busy={busy} />)}</tbody>
                </table>
              )}
        </DialogContent>
      </Dialog>
    </>
  );
};
